import React, { useState, useEffect } from 'react';
import { Search, ExternalLink, Award, Calendar, User, ArrowRight, Loader2 } from 'lucide-react';
import { internshipsApi } from '../../services/api';

const InternshipListing = () => {
  const [internships, setInternships] = useState([]);
  const [filteredInternships, setFilteredInternships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchInternships = async () => {
      try {
        setLoading(true);
        // Using the public endpoint specifically
        const response = await internshipsApi.getPublicInternships();
        setInternships(response.data);
      } catch (error) {
        console.error("Failed to load directory:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchInternships();
  }, []);

  useEffect(() => {
    const filtered = internships.filter(it => 
      it.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      it.certificateId.toLowerCase().includes(searchTerm.toLowerCase()) ||
      it.role.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredInternships(filtered);
  }, [searchTerm, internships]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0a0a]">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-[#0f5d9a] mx-auto mb-4" />
          <p className="text-gray-400 font-medium tracking-widest uppercase text-sm">Loading Directory...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-silver selection:bg-[#0f5d9a]/30">
      {/* Hero Section */}
      <div className="relative pt-24 pb-16 px-6 overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-[500px] bg-gradient-to-b from-[#0f5d9a]/10 to-transparent blur-3xl rounded-full opacity-50"></div>
        
        <div className="max-w-6xl mx-auto relative z-10 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-900/20 border border-blue-800/30 text-blue-400 text-xs font-bold tracking-[.2em] uppercase mb-6">
            <Award size={14} /> Official Certification Directory
          </div>
          <h1 className="text-5xl md:text-7xl font-black mb-6 tracking-tighter bg-gradient-to-b from-white to-gray-400 bg-clip-text text-transparent">
            Verify Excellence.
          </h1>
          <p className="text-gray-400 text-lg md:text-xl max-w-2xl mx-auto leading-relaxed">
            A comprehensive directory of talented individuals who have successfully completed their professional internships at Hara XY Innovation Lab.
          </p>
        </div>
      </div>

      {/* Directory Controls */}
      <div className="max-w-6xl mx-auto px-6 mb-12">
        <div className="glass-morphism rounded-2xl p-6 border border-white/5 flex flex-col md:flex-row gap-4 items-center">
          <div className="relative flex-1 w-full">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" size={20} />
            <input 
              type="text" 
              placeholder="Search by name, certificate ID, or role..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-black/40 border border-white/10 rounded-xl py-4 pl-12 pr-6 text-silver placeholder:text-gray-600 outline-none focus:border-[#0f5d9a]/50 transition-all duration-300"
            />
          </div>
          <div className="text-gray-500 text-sm whitespace-nowrap">
            Showing <span className="text-[#0f5d9a] font-bold">{filteredInternships.length}</span> verified talents
          </div>
        </div>
      </div>

      {/* Directory Grid */}
      <div className="max-w-6xl mx-auto px-6 pb-24">
        {filteredInternships.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredInternships.map((it) => (
              <div 
                key={it.id} 
                className="group relative bg-[#111] border border-white/5 rounded-2xl p-6 hover:border-[#0f5d9a]/30 transition-all duration-500 hover:-translate-y-1 shadow-2xl"
              >
                <div className="flex justify-between items-start mb-6">
                  <div className="bg-[#0f5d9a]/10 p-3 rounded-xl border border-[#0f5d9a]/20 text-[#0f5d9a]">
                    <User size={24} />
                  </div>
                  <span className="text-[10px] font-black tracking-widest text-[#0f5d9a] uppercase bg-[#0f5d9a]/5 px-2 py-1 rounded">
                    Verified
                  </span>
                </div>

                <h3 className="text-xl font-bold text-white mb-1 group-hover:text-[#0f5d9a] transition-colors uppercase">
                  {it.name}
                </h3>
                <p className="text-gray-500 text-sm mb-6 flex items-center gap-2">
                  <Award size={14} className="text-[#0f5d9a]" /> {it.role}
                </p>

                <div className="space-y-3 mb-8">
                  <div className="flex items-center gap-3 text-xs text-gray-600">
                    <Calendar size={14} />
                    Issued on {new Date(it.issueDate).toLocaleDateString()}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-gray-600 font-mono">
                    <ArrowRight size={14} />
                    {it.certificateId}
                  </div>
                </div>

                <a 
                  href={`/internship-certificates/${it.certificateId}`} 
                  className="w-full flex items-center justify-center gap-2 py-3 bg-white text-black font-bold rounded-xl hover:bg-[#0f5d9a] hover:text-white transition-all duration-300"
                >
                  View Certificate <ExternalLink size={16} />
                </a>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-20 bg-white/5 rounded-3xl border border-dashed border-white/10">
            <div className="text-gray-600 mb-4">No certification records found matching your search.</div>
            <button 
              onClick={() => setSearchTerm('')}
              className="text-[#0f5d9a] font-bold hover:underline"
            >
              Clear all filters
            </button>
          </div>
        )}
      </div>

      <style>{`
        .glass-morphism {
          background: rgba(255, 255, 255, 0.03);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
        }
      `}</style>
    </div>
  );
};

export default InternshipListing;
